/*
 * PhoneInput — E.164-validating phone input with inline hint.
 * Accepts +1XXXXXXXXXX only. Auto-prefixes "+1" if the user types 10 digits.
 * Exposes an isValid() helper via a `valid` prop that consumers can read.
 *
 * Usage:
 *   <PhoneInput value={phone} onChange={setPhone} testid="rider-phone" />
 *   const ok = isValidE164(phone);  // named export for pre-submit gate
 */
import React, { useMemo } from "react";
import { Phone as PhoneIcon } from "lucide-react";

const E164_RE = /^\+1\d{10}$/;

export function isValidE164(v) {
  return E164_RE.test((v || "").trim());
}

/**
 * autoPrefixUS — the one normalization rule used everywhere a phone number
 * is typed across the app (rider/driver/TP/facility/partner/internal-team
 * forms, broker forward fields, the Floor's call/invite panels). If someone
 * typed 10 digits (or 11 starting with a leading 1), silently prefix "+1"
 * so they never have to type it themselves. Leaves anything already
 * starting with "+" untouched.
 */
export function autoPrefixUS(raw) {
  const v = (raw || "").trim();
  if (!v || v.startsWith("+")) return v;
  const digits = v.replace(/[^\d]/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return v;
}

export default function PhoneInput({
  value,
  onChange,
  testid,
  placeholder = "+1XXXXXXXXXX",
  required = false,
  className = "",
  disabled = false,
}) {
  const normalized = useMemo(() => {
    const raw = (value || "").trim();
    if (!raw) return "";
    // If user typed 10 digits, auto-prefix +1
    const digits = raw.replace(/[^\d]/g, "");
    if (raw.startsWith("+")) return raw;
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    return raw;
  }, [value]);

  const valid = !value ? !required : isValidE164(normalized);
  const showError = !!value && !valid;

  return (
    <div className="relative">
      <PhoneIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
      <input
        type="tel"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          // On blur, auto-normalize to E.164 if we can
          if (normalized && normalized !== value) onChange(normalized);
        }}
        placeholder={placeholder}
        data-testid={testid}
        required={required}
        disabled={disabled}
        aria-invalid={showError}
        className={`w-full pl-9 border rounded-md px-3 py-2 text-sm ${
          showError ? "border-red-400 focus:border-red-500" : "border-[#E2E0D8]"
        } ${className}`}
      />
      {showError && (
        <div className="text-[11px] text-red-600 mt-1" data-testid={testid ? `${testid}-error` : undefined}>
          Use E.164 format: +1 followed by 10 digits (e.g. +18327225177)
        </div>
      )}
    </div>
  );
}
